import { useEffect, useState } from 'react'

import { useAuthHook } from './useAuthHook'

import { useAspirantsApi } from '@/services/api/aspirantsApi'
import { useAuthStore } from '@/store/authStore'
import { ErrorHandlingStrategy, MessageLevel } from '@/types/api'
import { saveStudyModality } from '@/utils/studyModalityHelper'

/**
 * Hook que carga automáticamente el studyModality del aspirante
 * cuando el usuario inicia sesión - Sigue el patrón de useInscriptionPreload
 */
export function useStudyModalityLoader() {
    const { isAuthenticated, idAspirant } = useAuthHook()
    const { updateStudyModality, studyModality } = useAuthStore()
    const [hasLoadedStudyModality, setHasLoadedStudyModality] = useState(false)

    const aspirantsApi = useAspirantsApi({
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY,
        errorHandling: ErrorHandlingStrategy.RETURN
    })

    // Cargar cuando el usuario está autenticado y no hemos cargado aún
    useEffect(() => {
        // Validaciones tempranas
        if (!isAuthenticated || !idAspirant || hasLoadedStudyModality) {
            return
        }

        // Si ya existe studyModality, marcar como cargado
        if (studyModality) {
            setHasLoadedStudyModality(true)

            return
        }

        const loadStudyModality = async () => {
            try {
                const { data, status } =
                    await aspirantsApi.getAcademicAdmission(idAspirant)

                if (status === 200 && data?.studyModality !== undefined) {
                    const saved = saveStudyModality(
                        data.studyModality,
                        updateStudyModality,
                        'StudyModalityLoader'
                    )

                    if (saved) {
                        setHasLoadedStudyModality(true)
                    }
                }
            } catch (error) {
                if (process.env.NODE_ENV === 'development') {
                    console.error(
                        '[StudyModalityLoader] Error al cargar:',
                        error
                    )
                }
            }
        }

        loadStudyModality()
    }, [
        isAuthenticated,
        idAspirant,
        hasLoadedStudyModality,
        studyModality,
        aspirantsApi,
        updateStudyModality
    ])

    // Limpiar cuando el usuario cierra sesión
    useEffect(() => {
        if (!isAuthenticated && hasLoadedStudyModality) {
            setHasLoadedStudyModality(false)
        }
    }, [isAuthenticated, hasLoadedStudyModality])

    return {
        studyModality,
        isLoading: !hasLoadedStudyModality,
        hasLoaded: hasLoadedStudyModality
    }
}
